import { useState, useEffect, useRef } from "react";
import { createTheme } from "@mui/material";

export function changeTimeToLocal(events) {
  return events.map((event) => ({ ...event, time: event.time.slice(0, -1) }));
}

/**
 * Parse an API launch timestamp as UTC milliseconds.
 * API times are UTC; callers often strip the trailing "Z" so wall-clock display
 * works with local getters. Without re-applying UTC, Date treats those strings
 * as local time and countdown / comparisons are offset by the browser timezone.
 */
export function parseUtcMs(time) {
  if (time == null || time === "") {
    return NaN;
  }

  const raw = String(time).trim();
  if (!raw) {
    return NaN;
  }

  // Already timezone-aware (Z or ±HH:MM / ±HHMM).
  if (/[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    return new Date(raw).valueOf();
  }

  // Naive ISO datetime or date-only — treat as UTC.
  if (raw.includes("T")) {
    return new Date(`${raw}Z`).valueOf();
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return new Date(`${raw}T00:00:00Z`).valueOf();
  }

  return new Date(raw).valueOf();
}

export function formatTime(time) {
  if (time === null) {
    return null;
  }
  // Prefer UTC components so the label matches real UTC even if "Z" was stripped.
  const ms = parseUtcMs(time);
  const date = new Date(ms);

  if (Number.isNaN(ms)) {
    return null;
  }

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);

  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");

  return `${formattedDate} ${hours}:${minutes} UTC`;
}

export function convertToUTC(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // Build a UTC string without changing the local time
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
}

export function allValuesTrue(dict) {
  if (typeof dict === "boolean") {
    return dict;
  }
  if (typeof dict === "object" && dict !== null) {
    return Object.values(dict).every((value) => allValuesTrue(value));
  }
}

export function setAllValuesTrue(dict) {
  const newDict = {};
  for (let key in dict) {
    if (typeof dict[key] === "object" && dict[key] !== null) {
      newDict[key] = setAllValuesTrue(dict[key]);
    } else if (typeof dict[key] === "boolean") {
      newDict[key] = true;
    } else {
      newDict[key] = dict[key];
    }
  }
  return newDict;
}

/** Fix UTF-8 text that was stored as Latin-1 (e.g. Sunâ€“Earth L2 → Sun–Earth L2). */
export function repairMojibake(value) {
  const str = String(value || "");
  if (!str) {
    return str;
  }

  const codes = [];
  for (const character of str) {
    const code = character.charCodeAt(0);
    if (code > 255) {
      return str;
    }
    codes.push(code);
  }

  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(Uint8Array.from(codes));
  } catch (error) {
    return str;
  }
}

export function toTitleCase(str) {
  if (typeof str === "string") {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  }
  return null;
}

function concatenatedList(items) {
  if (items.length === 1) {
    return items[0];
  } else if (items.length > 1) {
    const lastItem = items.pop();
    const separator = items.length > 1 ? ", and " : " and ";
    return items.join(", ") + separator + lastItem;
  } else {
    return "N/A";
  }
}

export function makeRecoveryString(recoveries, zones) {
  const recoveriesList = recoveries.map((recovery) => {
    if (recovery.landing_zone) {
      const zone =
        typeof recovery.landing_zone === "object"
          ? recovery.landing_zone
          : zones.find((zone) => zone.id === recovery.landing_zone);
      return `${zone?.nickname} (${toTitleCase(recovery.method_success).replace("Tbd", "TBD")})`;
    } else if (recovery.method) {
      var tempStr = toTitleCase(recovery.method.replace("_", " "));

      if (recovery.method !== "EXPENDED") {
        tempStr = tempStr.concat(" ", `(${toTitleCase(recovery.method_success).replace("Tbd", "TBD")})`);
      }

      return tempStr;
    } else {
      return "Expended";
    }
  });

  return concatenatedList(recoveriesList);
}

export function versionFormat(str) {
  const regex = /V(\d)/g;
  if (regex.test(str)) {
    return str.replace(regex, "v$1");
  }
  return str;
}

export function numberWithCommas(x) {
  if (x === null) {
    return "Unknown";
  }
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function createPath(arr) {
  let str = "";
  for (const item of arr) {
    str += `${item}.`;
  }
  return str;
}

export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear the timeout if the value changes before the delay ends
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout to update the debounced value
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup the timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

export function filterLaunchByTime(data) {
  const now = new Date();
  const previousLaunches = [];
  const futureLaunches = [];

  changeTimeToLocal(data.launches).forEach((launch) => {
    const launchDate = new Date(launch.time);
    if (launchDate < now) {
      previousLaunches.push(launch);
    } else {
      futureLaunches.push(launch);
    }
  });

  const { launches, ...rest } = data; // Exclude the launches list from the original data

  return { ...rest, previousLaunches, futureLaunches };
}

export function getHostName() {
  if (window.location.hostname === "localhost") {
    return "http://127.0.0.1:8000";
  }
  return `https://api.${window.location.hostname}`;
}

export function getPhotoLocation() {
  if (window.location.hostname === "localhost") {
    return "http://127.0.0.1:8000";
  }
  return "";
}

export function convertSeconds(x) {
  if (x === null || x === undefined) {
    return "N/A";
  }

  let d = Math.floor(x / 86400);
  x -= d * 86400;
  let h = Math.floor(x / 3600);
  x -= h * 3600;
  let m = Math.floor(x / 60);
  x -= m * 60;
  let s = Math.round(x);

  const timeUnits = [];
  if (d) {
    timeUnits.push(`${d} day${d !== 1 ? "s" : ""}`);
  }
  if (h) {
    timeUnits.push(`${h} hour${h !== 1 ? "s" : ""}`);
  }
  if (m) {
    timeUnits.push(`${m} minute${m !== 1 ? "s" : ""}`);
  }
  if (s) {
    timeUnits.push(`${s} second${s !== 1 ? "s" : ""}`);
  }

  let timeStr;
  if (timeUnits.length > 2) {
    timeStr = timeUnits.slice(0, -1).join(", ") + ", and " + timeUnits[timeUnits.length - 1];
  } else if (timeUnits.length === 2) {
    timeStr = timeUnits[0] + " and " + timeUnits[1];
  } else if (timeUnits.length === 1) {
    timeStr = timeUnits[0];
  } else {
    timeStr = "0 seconds";
  }

  return timeStr;
}

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
});

export const customTheme = createTheme({
  palette: {
    primary: {
      main: "#4e4ecf",
    },
    background: {
      default: "rgb(31, 41, 55)",
      paper: "rgb(31, 41, 55)",
    },
    text: {
      primary: "#ffffff",
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "Roboto",
      "Oxygen",
      "Ubuntu",
      "Cantarell",
      "Fira Sans",
      "Droid Sans",
      "Helvetica Neue",
      "sans-serif",
    ].join(","),
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#ffffff", // Ensure label text is always white
          "&.Mui-focused": {
            color: "#ffffff", // Ensure label text remains white when focused
          },
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          color: "#ffffff",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#ffffff",
          "&.MuiTypography-root": {
            color: "#ffffff", // Ensures higher specificity
          },
        },
        overline: {
          color: "#ffffff",
          "&.MuiTypography-overline": {
            color: "#ffffff", // Ensures higher specificity
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": {
            borderColor: "rgba(154, 164, 181, 0.24)",
          },
          "&:hover fieldset": {
            borderColor: "rgba(35, 139, 184, 0.55)",
          },
          "&.Mui-focused fieldset": {
            borderColor: "rgba(35, 139, 184, 0.55)",
          },
          color: "#ffffff", // Text color inside the input
        },
        input: {
          color: "#ffffff", // Ensure input text is white
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: "#ffffff", // Ensure text color is white
          backgroundColor: "#2e2e4e", // Background color for select
          "&:before": {
            borderColor: "#3e3e6e", // Border color before the component is interacted with
          },
          "&:after": {
            borderColor: "#3e3e6e", // Border color after the component is interacted with
          },
        },
        icon: {
          color: "#ffffff", // Ensure the dropdown icon color is white
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        sizeMedium: {
          color: "#ffffff",
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: "#ffffff",
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        header: {
          color: "#ffffff",
        },
        weekDayLabel: {
          color: "#ffffff",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: "#2e2e4e",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #4e4ecf",
        },
        indicator: {
          backgroundColor: "#4e4ecf",
        },
        flexContainer: {
          justifyContent: "center",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        textColorPrimary: {
          color: "#ffffff",
        },
        root: {
          fontSize: "1.5rem",
          fontWeight: "bold",
          textTransform: "none",
          minWidth: 72,
          marginRight: (theme) => theme.spacing(4),
          "&.Mui-selected": {
            color: "#ffffff",
            fontWeight: (theme) => theme.typography.fontWeightMedium,
          },
          "&.Mui-focusVisible": {
            backgroundColor: "#ffffff",
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          color: "#ffffff", // Ensure text color is white
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: "#ffffff", // Ensure menu item text color is white
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          margin: "0",
        },
      },
    },
  },
});
